/**
 * Computes the pixel position of a given character offset inside a
 * <textarea>, relative to the textarea's own top-left corner. Used to
 * anchor the @mention suggestion dropdown right where the user is typing,
 * instead of gluing it to the bottom of the box.
 *
 * Standard "mirror div" technique: build an off-screen div that exactly
 * replicates the textarea's font/box metrics, fill it with the text up to
 * the target position, and measure where a marker span lands.
 */

const MIRRORED_STYLE_PROPS = [
  'boxSizing',
  'width',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'borderStyle',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'fontStyle',
  'fontVariant',
  'fontWeight',
  'fontStretch',
  'fontSize',
  'lineHeight',
  'fontFamily',
  'textAlign',
  'textTransform',
  'textIndent',
  'textDecoration',
  'letterSpacing',
  'wordSpacing',
  'tabSize',
  'whiteSpace',
  'wordBreak',
  'wordWrap',
] as const

let mirrorDiv: HTMLDivElement | null = null

function getMirrorDiv(): HTMLDivElement {
  if (mirrorDiv) return mirrorDiv
  mirrorDiv = document.createElement('div')
  mirrorDiv.style.position = 'absolute'
  mirrorDiv.style.visibility = 'hidden'
  mirrorDiv.style.top = '0'
  mirrorDiv.style.left = '-9999px'
  mirrorDiv.style.whiteSpace = 'pre-wrap'
  mirrorDiv.style.wordWrap = 'break-word'
  document.body.appendChild(mirrorDiv)
  return mirrorDiv
}

export interface CaretCoordinates {
  top: number
  left: number
  height: number
}

/**
 * Returns the caret's pixel offset from the textarea's own top-left corner
 * (already accounting for the textarea's current scroll position).
 */
export function getCaretCoordinates(
  textarea: HTMLTextAreaElement,
  position: number
): CaretCoordinates {
  const div = getMirrorDiv()
  const computed = window.getComputedStyle(textarea)

  for (const prop of MIRRORED_STYLE_PROPS) {
    const kebabProp = prop.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)
    div.style.setProperty(kebabProp, computed.getPropertyValue(kebabProp))
  }

  div.textContent = textarea.value.substring(0, position)
  const span = document.createElement('span')
  // A non-empty marker guarantees a measurable box even at end-of-line.
  span.textContent = textarea.value.substring(position) || '.'
  div.appendChild(span)

  const coordinates: CaretCoordinates = {
    top: span.offsetTop - textarea.scrollTop,
    left: span.offsetLeft - textarea.scrollLeft,
    height: span.offsetHeight,
  }

  div.removeChild(span)
  return coordinates
}
